import { FieldValues, get } from 'react-hook-form';

import { isDefined, isEmpty, isNotEmpty, objectKeys, objectValues } from '@hsp/utilities';

import { IAttributeDataPoint } from '@t/api/audience/form/attribute.data-point';

import { filterFilterValues } from 'pages/audience/utilities';
import {
  FORM_NAMES,
  IAudienceSearchConditions,
  IMasterFile,
  ISearchFormValue,
  ISelectedFilter,
  SearchType,
} from 'types/api/audience';
import { IClient } from 'types/api/client';
import { IClientList } from 'types/api/client-list';
import { IExternalList } from 'types/api/external-list';

const checkFilters = <T = string>(item: ISelectedFilter<T>) => {
  if (!isDefined(item) || isEmpty(item)) {
    return false;
  }

  const { filters } = item;

  return isDefined(filters) && isNotEmpty(filters);
};

const validateBasicFieldValues = (fieldValues: FieldValues) => {
  fieldValues = { ...get(fieldValues, FORM_NAMES.Basic) };

  const keys = Object.keys(fieldValues);

  for (const key of keys) {
    const fieldValue = get(fieldValues, key);
    const values = objectValues(fieldValue) as ISearchFormValue[];

    if (values.some(({ value }) => isNotEmpty(value))) {
      return true;
    }
  }

  return false;
};

const validateConditionFieldValues = (fieldValues: FieldValues) => {
  fieldValues = { ...fieldValues[SearchType.Conditions] };

  const fieldValue = fieldValues as IAudienceSearchConditions;

  if (!isDefined(fieldValue)) {
    return false;
  }

  const { and, or } = fieldValue;

  const combined = objectValues([...(and ?? []), ...(or ?? [])]) as Record<string, Record<string, string>>[];

  if (isEmpty(combined)) {
    return false;
  }

  for (const item of combined) {
    if (isEmpty(item)) {
      return false;
    }

    const { category, dataPoint, ...rest } = item;
    const keys = Object.keys(rest);

    if (isEmpty(keys) || keys.some((key) => isEmpty(key))) {
      return false;
    }

    for (const key of keys) {
      const property = item[key];

      if (isEmpty(property)) {
        return false;
      }

      const propertyValues = objectKeys(property);

      for (const propertyValue of propertyValues) {
        const formValue = property[propertyValue];

        if (typeof formValue === 'string') {
          if (isEmpty(formValue)) {
            return false;
          }
        }

        if (typeof formValue === 'object') {
          const { value } = formValue;

          if (isEmpty(value)) {
            return false;
          }
        }
      }
    }
  }

  return true;
};

const validateSequenceFieldValues = (): boolean => {
  return false;
};

const validators: Record<string, (fieldValues: FieldValues, searchType?: SearchType) => boolean> = {
  validateAttributesFieldValues: (fieldValues: FieldValues) => {
    const excludeBusiness = get(fieldValues, FORM_NAMES.ExcludeBusiness);
    const includeNonDonor = get(fieldValues, FORM_NAMES.IncludeNonDonor);
    const includeOutOfStateDonor = get(fieldValues, FORM_NAMES.IncludeOutOfStateDonor);
    const gender = get(fieldValues, FORM_NAMES.Gender);

    if (isNotEmpty(excludeBusiness)) {
      const { value } = excludeBusiness as IAttributeDataPoint;

      if (value == 1) {
        return true;
      }
    }

    if (isNotEmpty(includeNonDonor)) {
      return true;
    }

    if (isNotEmpty(includeOutOfStateDonor)) {
      return true;
    }

    if (isNotEmpty(gender)) {
      return true;
    }

    return false;
  },
  validateDataSourceFieldValues: (fieldValues: FieldValues) => {
    const clients = get(fieldValues, FORM_NAMES.Clients) as ISelectedFilter<IClient>;
    const externalLists = get(fieldValues, FORM_NAMES.ExternalLists) as ISelectedFilter<IExternalList>;
    const lists = get(fieldValues, FORM_NAMES.Lists) as ISelectedFilter<IClientList>;
    const masterFile = get(fieldValues, FORM_NAMES.MasterFile) as IMasterFile | undefined;
    const thirdParty = get(fieldValues, FORM_NAMES.ThirdParty) as string | undefined;

    if (isDefined(masterFile) && isNotEmpty(masterFile)) {
      return true;
    }

    if (isDefined(thirdParty)) {
      return true;
    }

    const isExternalListsValid = checkFilters(externalLists);

    if (isExternalListsValid) {
      return true;
    }

    const isClientsValid = checkFilters(clients);

    if (!isClientsValid) {
      return false;
    }

    const { type } = lists;

    if (type === 'exclude') {
      return checkFilters(lists);
    }

    return true;
  },
  validateFilterValues: (fieldValues: FieldValues) => {
    const keys = filterFilterValues();
    let isValid = false;

    for (const key of keys) {
      const formValue = fieldValues[key];

      if (!isDefined(formValue) || isEmpty(formValue)) {
        continue;
      }

      switch (key) {
        case SearchType.Locations: {
          const locationKeys = Object.keys(formValue);

          for (const locationKey of locationKeys) {
            isValid = checkFilters(formValue[locationKey]);

            if (isValid) {
              return true;
            }
          }
          break;
        }

        default: {
          isValid = checkFilters(formValue);

          if (isValid) {
            return true;
          }
          break;
        }
      }
    }

    return isValid;
  },
  validateSearchFieldValues: (fieldValues: FieldValues, searchType?: SearchType) => {
    switch (searchType) {
      case SearchType.Basic:
        return validateBasicFieldValues(fieldValues);

      case SearchType.Conditions:
        return validateConditionFieldValues(fieldValues);

      case SearchType.Sequences:
        return validateSequenceFieldValues();

      default:
        return false;
    }
  },
  validateTags: (fieldValues: FieldValues) => {
    fieldValues = { ...get(fieldValues, SearchType.Tags) };

    const keys = Object.keys(fieldValues);

    for (const key of keys) {
      const formValue = get(fieldValues, key);

      if (checkFilters(formValue)) {
        return true;
      }
    }

    return false;
  },
};

export const isValidFieldValues = (searchType: SearchType, fieldValues: FieldValues): boolean => {
  const keys = Object.keys(validators);

  for (const key of keys) {
    const validator = get(validators, key);
    const isValid = validator.call(validator, fieldValues, searchType);

    if (isValid) {
      return true;
    }
  }

  return false;
};
